/**
 * Preferencias y mensajes del buzón del equipo (MVP en localStorage).
 * En producción: endpoint dedicado, sin identidad en el mensaje, y políticas legales/RR.HH.
 */
import {
  runTransaction,
  txRead,
  txReadRaw,
  txWrite,
} from "@/lib/persistence/transactionalStore";
const SETTINGS_KEY = "altenot_buzon_equipo_settings_v1";
const MESSAGES_KEY = "altenot_buzon_equipo_mensajes_v1";
/** Última publicación en este navegador (MVP; en producción lo controla el servidor). */
const LAST_PUBLISH_KEY = "altenot_buzon_equipo_last_publish_v1";
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

export const BUZON_EQUIPO_CHANGED_EVENT = "buzon-equipo-changed";

export interface BuzonEquipoSettings {
  enabled: boolean;
}

export interface BuzonMensaje {
  id: string;
  texto: string;
  categoria: string;
  createdAt: string;
}

function emitChanged() {
  window.dispatchEvent(new Event(BUZON_EQUIPO_CHANGED_EVENT));
}

export function getBuzonEquipoSettings(): BuzonEquipoSettings {
  const p = txRead<Partial<BuzonEquipoSettings>>(SETTINGS_KEY, {
    enabled: false,
  });
  return { enabled: Boolean(p.enabled) };
}

export function getBuzonEquipoEnabled(): boolean {
  return getBuzonEquipoSettings().enabled;
}

export function setBuzonEquipoEnabled(enabled: boolean) {
  txWrite(SETTINGS_KEY, { enabled });
  emitChanged();
}

export function getBuzonPublishCooldownRemainingMs(): number {
  try {
    const raw = txReadRaw(LAST_PUBLISH_KEY);
    if (!raw) return 0;
    const last = new Date(raw).getTime();
    if (Number.isNaN(last)) return 0;
    return Math.max(0, COOLDOWN_MS - (Date.now() - last));
  } catch {
    return 0;
  }
}

export function canPublishBuzonMensaje(): boolean {
  return getBuzonPublishCooldownRemainingMs() === 0;
}

export function getBuzonMensajes(): BuzonMensaje[] {
  return txRead(MESSAGES_KEY, []) as BuzonMensaje[];
}

export function addBuzonMensaje(
  texto: string,
  categoria: string,
):
  | { ok: true; mensaje: BuzonMensaje }
  | { ok: false; reason: "cooldown"; remainingMs: number } {
  const remainingMs = getBuzonPublishCooldownRemainingMs();
  if (remainingMs > 0) {
    return { ok: false, reason: "cooldown", remainingMs };
  }
  const row: BuzonMensaje = {
    id: `buz_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    texto: texto.trim(),
    categoria,
    createdAt: new Date().toISOString(),
  };
  runTransaction(() => {
    const list = getBuzonMensajes();
    txWrite(MESSAGES_KEY, [row, ...list]);
    txWrite(LAST_PUBLISH_KEY, new Date().toISOString());
  });
  emitChanged();
  return { ok: true, mensaje: row };
}
